import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, Select, MenuItem, Typography, Snackbar, Alert } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import { fetchTopics, createThread, fetchUserThreads } from '../services/api';
import { useInstitution } from './InstitutionContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import getUserDisplayName from '../utils/getUserDisplayName';

export default function AddPostDialog({
  open,
  onClose,
  previousPath,
  onSuccess,
  onError,
  onAdded,
}) {
  const { selectedInstitution } = useInstitution();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [topicId, setTopicId] = useState('');
  const [subtopicId, setSubtopicId] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      if (!selectedInstitution) {
        setTopics([]);
        return;
      }
      try {
        const data = await fetchTopics(selectedInstitution);
        setTopics(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load topics', err);
      }
    };
    load();
    setTopicId('');
    setSubtopicId('');
    setTitle('');
    setContent('');
  }, [open, selectedInstitution]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!topicId || !title || !content) return;
    try {
      const payload = {
        title,
        content,
        topic: topicId,
        subtopic: subtopicId || null,
      };
      const newThread = await createThread(payload);
      let isFirstThread = false;
      try {
        const displayName = getUserDisplayName(user);
        const instId = selectedInstitution || user?.userprofile?.institution?.id;
        const mine = await fetchUserThreads(displayName, instId);
        const list = mine.results || mine;
        if (Array.isArray(list) && list.length === 1) {
          isFirstThread = true;
        }
      } catch (err) {
        console.error('Failed to fetch my threads:', err.response?.data || err.message);
      }
      if (onAdded) await onAdded(newThread, isFirstThread);
      const msg = 'Post submitted';
      if (onSuccess) onSuccess(msg); else setSuccessMessage(msg);
      onClose();
      navigate(`/community/threads/${newThread.id}`, { state: { from: previousPath } });
    } catch (err) {
      console.error('Failed to create thread', err);
      if (onError) onError('Failed to create thread.');
    }
  };

  const selectedTopic = topics.find((t) => String(t.id) === String(topicId));
  const subtopics = selectedTopic?.subtopics || [];

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ component: 'form', onSubmit: handleSubmit }}>
      <DialogTitle>Submit a Post</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <FormControl fullWidth>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Topic <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Select
            value={topicId}
            onChange={(e) => {
              setTopicId(e.target.value);
              setSubtopicId('');
            }}
            size="small"
          >
            {topics.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={!subtopics.length}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Subtopic
          </Typography>
          <Select
            value={subtopicId}
            onChange={(e) => setSubtopicId(e.target.value)}
            size="small"
          >
            {subtopics.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Title <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
        </div>
        <div>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Content <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Editor
            tinymceScriptSrc="/tinymce/tinymce.min.js"
            value={content}
            init={{
              license_key: 'gpl',
              height: 300,
              menubar: false,
              plugins: ['link', 'lists', 'autolink', 'charmap', 'table'],
              toolbar: 'undo redo | bold italic underline | bullist numlist outdent indent',
            }}
            onEditorChange={(c) => setContent(c)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="success" />
        <FilledButton type="submit" text="Post" color="success" disabled={!topicId || !title || !content} />
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
        {successMessage}
      </Alert>
    </Snackbar>
    </>
  );
}

